/**
 * DeliveredScreen — read-only delivery confirmation (mockup #s-delivered).
 *
 * Reads the hoisted report stream (no second listener): the latest report doc +
 * its status. Shows the delivered version, the delivery channel note, and a PDF
 * Download/View — enabled ONLY when the report is delivered. The server
 * (getReportDownloadUrl CF) is the SOLE entitlement authority: we never decide
 * client-side; a denied/expired request surfaces its `.message` verbatim. For a
 * draft we show "available after delivery" and do NOT call the CF (it 404s on
 * drafts).
 */
import { useState } from 'react';
import { Box, Btn3, Tag } from '../ui';
import {
  getReportDownloadUrl,
  type ReportDoc,
  type ReportStatus,
} from '../../lib/reportDelivery';
import type { DeliverableTier } from '../../lib/reportDelivery';
import type { L1ATabKey } from '../../lib/l1aTabs';

export default function DeliveredScreen({
  pid,
  clientId,
  deliverableTier,
  latest,
  status,
  onMsg,
  onGo,
}: {
  pid: string;
  clientId: string | null;
  deliverableTier: DeliverableTier | null;
  latest: ReportDoc | null;
  /** null = no report generated yet. */
  status: ReportStatus | null;
  onMsg: (m: string) => void;
  onGo: (key: L1ATabKey) => void;
}) {
  const [busy, setBusy] = useState(false);
  const delivered = status === 'delivered';

  async function openPdf() {
    if (busy || !delivered || !clientId || !deliverableTier || !latest) return;
    setBusy(true);
    try {
      const res = await getReportDownloadUrl({
        clientId,
        projectId: pid,
        tier: deliverableTier,
        runId: latest.run_id,
        versionN: latest.version_n,
      });
      window.open(res.url, '_blank');
    } catch (e) {
      // The server is the sole authority — surface its message verbatim, never crash.
      onMsg((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box title="Delivery record" note="Sent over the client's channel + portal">
      <div className="l1a-screen">
        <div className="kv">
          <span className="k">Delivered version</span>
          {delivered && deliverableTier && latest ? (
            <b>
              {deliverableTier} v{latest.version_n ?? 1}
            </b>
          ) : (
            <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
              <Tag>not delivered yet</Tag>
              <Btn3 intent="ghost" size="sm" onClick={() => onGo('rendered')}>
                Deliver from Report render →
              </Btn3>
            </span>
          )}
        </div>
        <div className="kv">
          <span className="k">Status</span>
          {status === 'delivered' ? (
            <Tag kind="val">delivered</Tag>
          ) : status === 'approved' ? (
            <Tag kind="val">approved</Tag>
          ) : status === 'draft' ? (
            <Tag kind="warn">draft</Tag>
          ) : (
            <Tag>not generated</Tag>
          )}
        </div>
        <div className="kv">
          <span className="k">Channel</span>
          <span style={{ color: 'var(--ink-mute)', fontSize: 12.5 }}>WhatsApp + portal download</span>
        </div>
        <div className="kv">
          <span className="k">PDF</span>
          {delivered ? (
            <Btn3 intent="au" size="sm" disabled={busy} onClick={openPdf}>
              {busy ? 'Opening…' : 'Download / View'}
            </Btn3>
          ) : (
            <Tag>available after delivery</Tag>
          )}
        </div>
      </div>
    </Box>
  );
}
